import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { Party } from 'api/file/interfaces/get-file-order-insights';
import DomesticAddressField from 'features/files/file-create/DomesticAddress';
import { useEffect, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetTextInput } from 'ui-kit/inputs';
import PhoneField from 'ui-kit/inputs/PhoneField/PhoneField';
import { domesticAddressSchema, orderInsightEmailSchema, orderInsightPartySchema } from 'utilities/validation/schemas';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';

interface PhoneNumberModalProps {
  onClose: () => void;
  onConfirm: (value: Party) => void;
  isActionInProgress: boolean;
  disablePrimary: boolean;
  open: boolean;
  party: Party;
  qaPrefix: string;
  index: number;
  type: 'ContactValidation' | 'EnhancedIdentityVerification';
}

const useStyles = makeStyles((theme) => ({
  phoneNumberContainer: {
    marginBottom: theme.spacing(2),
  },
  dialogWrap: {
    minWidth: 600,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  dialogClose: {
    padding: 0,
  },
  dialogContent: {
    padding: pxToRem(24),
  },
}));

const PhoneNumberModal = ({
  party,
  onClose,
  onConfirm,
  isActionInProgress,
  disablePrimary,
  open,
  type,
  qaPrefix,
  index,
}: PhoneNumberModalProps) => {
  const isContactValidation = type === 'ContactValidation';
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isFormValid, setFormValid] = useState({
    isDomesticAddressFormValid: false,
    isPhoneOrEmailFormValid: false,
  });
  const [currentParty, setCurrentParty] = useState<Party>(party);

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const classes = useStyles();

  const validateErrors = async (party: Party) => {
    const errs =
      (await doValidate(party, isContactValidation ? orderInsightPartySchema : orderInsightEmailSchema)) || [];
    setValidationErrors(errs);
    setFormValid((prev) => ({ ...prev, isPhoneOrEmailFormValid: errs.length === 0 }));
  };

  useEffect(() => {
    if (open) validateErrors(party);
  }, [open]);

  const handleCloseDialog = () => {
    // resetting the state on dailog close
    setIsSubmitted(false);
    setFormValid({
      isDomesticAddressFormValid: false,
      isPhoneOrEmailFormValid: false,
    });
    setValidationErrors([]);
    setCurrentParty(party);
    onClose();
  };

  const saveAndValidate = () => {
    setIsSubmitted(true);
    validateErrors(currentParty);
    if ((!isContactValidation || isFormValid.isDomesticAddressFormValid) && isFormValid.isPhoneOrEmailFormValid) {
      onConfirm(currentParty);
    }
  };

  const onChangeWithValidation = async (newValue: Party) => {
    validateErrors(newValue);
    setCurrentParty(newValue);
  };

  useEffect(() => {
    validateErrors(party);
  }, [party]);

  const isPhoneNumberRequired = useMemo(() => {
    if (
      !isContactValidation ||
      ((currentParty?.emailAddress?.trim() ?? '').length > 0 && (currentParty?.phoneNumber ?? '').length > 0)
    ) {
      return true;
    }

    return (currentParty?.emailAddress?.trim() ?? '').length === 0;
  }, [currentParty?.emailAddress, currentParty?.phoneNumber, type]);

  const isEmailAddressRequired = useMemo(() => {
    if (!isContactValidation) {
      return false;
    }

    if ((currentParty?.emailAddress?.trim() ?? '').length > 0 && (currentParty?.phoneNumber ?? '').length > 0) {
      return true;
    }

    return (currentParty?.phoneNumber ?? '').length === 0;
  }, [currentParty?.emailAddress, currentParty?.phoneNumber, type]);

  return (
    <Dialog
      onClose={handleCloseDialog}
      aria-labelledby="contact-us"
      open={open}
      maxWidth={'xs'}
      PaperProps={{ className: classes.dialogWrap }}
    >
      <DialogTitle>
        <div className={classes.dialogTitle}>
          <Typography variant="h3" color="textPrimary">
            Edit {currentParty.partyName}
          </Typography>
          <IconButton
            color="secondary"
            data-testid={`${qaPrefix}-button-close-${index}`}
            className={classes.dialogClose}
            onClick={handleCloseDialog}
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container spacing={3} className={isContactValidation ? classes.phoneNumberContainer : ''}>
          {isContactValidation && (
            <Grid item xs={12}>
              <Typography variant="body1">
                Any phone number, email address, or street address you provide will be validated.
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <PhoneField
              variant="outlined"
              data-testid={`${qaPrefix}-input-phone-number-${index}`}
              required={isPhoneNumberRequired}
              name={'phoneNumber'}
              label={'Phone Number'}
              value={currentParty?.phoneNumber ?? ''}
              showValidation={isSubmitted}
              errs={validationErrors}
              fullWidth
              onChange={(e: any) => {
                onChangeWithValidation({ ...currentParty, phoneNumber: e?.target?.value });
              }}
            />
          </Grid>
          {isContactValidation && (
            <Grid item xs={6}>
              <AgentNetTextInput
                variant="outlined"
                fullWidth
                required={isEmailAddressRequired}
                name="emailAddress"
                showValidation={isSubmitted}
                errs={validationErrors}
                data-testid={`${qaPrefix}-input-email-${index}`}
                label={'Email'}
                value={currentParty?.emailAddress?.trim() ?? ''}
                onChange={(e) => {
                  onChangeWithValidation({ ...currentParty, emailAddress: e.target.value.trim() ?? '' });
                }}
              />
            </Grid>
          )}
        </Grid>
        {isContactValidation && (
          <Grid item xs={12}>
            <DomesticAddressField
              showCounty={false}
              disableState={false}
              schema={domesticAddressSchema}
              updatedQAAttributes={{
                address1: `${qaPrefix}-input-address1-${index}`,
                address2: `${qaPrefix}-input-address2-${index}`,
                city: `${qaPrefix}-input-city-${index}`,
                state: `${qaPrefix}-input-state-${index}`,
                postalCode: `${qaPrefix}-input-zip-${index}`,
                county: `${qaPrefix}-input-county-${index}`,
              }}
              isOutsideJurisdiction={true}
              setIsValid={(isValid) => {
                setFormValid((prev) => ({ ...prev, isDomesticAddressFormValid: isValid }));
              }}
              displayRequiredAddressFields={false}
              value={{
                address1: currentParty.address?.address1 ?? '',
                address2: currentParty.address?.address2 ?? '',
                city: currentParty.address?.city ?? '',
                postalCode: currentParty.address?.postalCode ?? '',
                stateOrProvince: currentParty.address?.stateOrProvince ?? '',
              }}
              stateOptions={false}
              showAllValidation={isSubmitted}
              useGoogleAutocomplete={true}
              onChange={(e) => {
                setCurrentParty((prev) => ({ ...prev, address: { ...prev.address, ...e } }));
              }}
            />
          </Grid>
        )}
      </DialogContent>
      <DialogActions className="modal-btn-container ghost-white flex-sb p-16-24">
        <AgentNetButton
          variant="outlined"
          color="primary"
          data-testid={`${qaPrefix}-button-cancel-${index}`}
          onClick={handleCloseDialog}
        >
          Cancel
        </AgentNetButton>
        <AgentNetButton
          color="primary"
          variant="contained"
          autoFocus
          onClick={saveAndValidate}
          data-testid={`${qaPrefix}-button-save-validate-${index}`}
          loading={isActionInProgress}
          disabled={isActionInProgress || disablePrimary}
        >
          Save & Validate
        </AgentNetButton>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumberModal;
